<script lang="ts">
  import type { StepType } from '$lib/model/types';
  import { projectStore, selectStep } from '$lib/state/project.svelte';
  import {
    deleteStep,
    renameStep,
    isExportableLeaf,
  } from '$lib/model/tree';
  import { allowedChildren, isVirtualNode } from '$lib/model/validity';
  import { triggerRecompute } from '$lib/compute/recompute.svelte';
  import AddStepMenu from './AddStepMenu.svelte';
  import StepRow from './StepRow.svelte';

  interface Props {
    stepId: string;
    depth: number;
  }

  const { stepId, depth }: Props = $props();

  const step = $derived(projectStore.pipeline.steps[stepId]);
  const selected = $derived(projectStore.selected_step_id === stepId);
  const isLeaf = $derived(step ? isExportableLeaf(step) : false);
  const canAddChild = $derived(
    step ? allowedChildren(step.type).length > 0 : false,
  );
  const isRoot = $derived(stepId === projectStore.pipeline.root_id);
  const isVirtual = $derived(step ? isVirtualNode(step.type) : false);

  let renaming = $state(false);
  let nameInput = $state('');
  let menuOpen = $state(false);

  function iconFor(type: StepType): string {
    switch (type) {
      case 'input':
        return '📷';
      case 'greyscale':
        return '◐';
      case 'color_separate':
        return '🎨';
      case 'channel':
        return '⬤';
      case 'gradient_sep':
        return '⬛';
      case 'band':
        return '▣';
      case 'halftone':
        return '●';
    }
  }

  function startRename(e: Event) {
    e.stopPropagation();
    if (!step) return;
    nameInput = step.name;
    renaming = true;
  }
  function commitRename() {
    const trimmed = nameInput.trim();
    if (trimmed && step) {
      renameStep(projectStore.pipeline, stepId, trimmed);
      triggerRecompute();
    }
    renaming = false;
  }
  function cancelRename() {
    renaming = false;
  }
  function onRowClick() {
    selectStep(stepId);
  }
  function onDelete(e: Event) {
    e.stopPropagation();
    if (!step) return;
    const ok =
      typeof window !== 'undefined' && window.confirm
        ? window.confirm(`Delete "${step.name}" and its children?`)
        : true;
    if (!ok) return;
    const removed = deleteStep(projectStore.pipeline, stepId);
    projectStore.layer_order = projectStore.layer_order.filter(
      (id) => !removed.includes(id),
    );
    if (removed.includes(projectStore.selected_step_id ?? '')) {
      selectStep(projectStore.pipeline.root_id);
    }
    triggerRecompute();
  }
  function toggleMenu(e: Event) {
    e.stopPropagation();
    menuOpen = !menuOpen;
  }
</script>

{#if step}
  <div
    class="row"
    class:selected
    style="--depth: {depth};"
    onclick={onRowClick}
    onkeydown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRowClick();
      }
    }}
    role="treeitem"
    tabindex="0"
    aria-selected={selected}
  >
    <span class="indent" aria-hidden="true"></span>
    <span class="icon">{iconFor(step.type)}</span>
    {#if renaming}
      <input
        class="rename"
        bind:value={nameInput}
        onkeydown={(e) => {
          if (e.key === 'Enter') commitRename();
          else if (e.key === 'Escape') cancelRename();
        }}
        onblur={commitRename}
        onclick={(e) => e.stopPropagation()}
      />
    {:else}
      <span
        class="name"
        ondblclick={startRename}
        onkeydown={(e) => {
          if (e.key === 'F2') startRename(e);
        }}
        role="button"
        tabindex="-1"
      >
        {step.name}
      </span>
    {/if}
    {#if isLeaf}<span class="leaf-mark" title="exportable">✓</span>{/if}
    <span class="spacer"></span>
    {#if canAddChild}
      <button
        class="ico"
        title="Add child step"
        onclick={toggleMenu}
        aria-label="Add child step"
      >
        +
      </button>
    {/if}
    {#if !isRoot && !isVirtual}
      <button
        class="ico"
        title="Delete step"
        onclick={onDelete}
        aria-label="Delete step"
      >
        ×
      </button>
    {/if}
  </div>

  {#if menuOpen}
    <AddStepMenu parentStepId={stepId} onClose={() => (menuOpen = false)} />
  {/if}

  {#each step.children as childId (childId)}
    <StepRow stepId={childId} depth={depth + 1} />
  {/each}
{/if}

<style>
  .row {
    display: grid;
    grid-template-columns: calc(var(--depth) * 16px) max-content 1fr auto auto auto;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 13px;
    background: transparent;
  }
  .row:hover {
    background: var(--surface-2);
  }
  .row.selected {
    background: var(--accent-dim);
    color: var(--bg);
  }
  .row.selected .leaf-mark,
  .row.selected .icon {
    color: var(--bg);
  }
  .indent {
    width: calc(var(--depth) * 16px);
  }
  .icon {
    width: 18px;
    text-align: center;
  }
  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .rename {
    width: 100%;
    padding: 2px 6px;
    font-size: 13px;
  }
  .leaf-mark {
    color: var(--ok);
    font-weight: 600;
  }
  .ico {
    padding: 0 6px;
    font-size: 14px;
    line-height: 1;
    background: transparent;
    border: 1px solid transparent;
  }
  .ico:hover {
    background: var(--surface);
    border-color: var(--border);
  }
  .row.selected .ico {
    color: var(--bg);
  }
</style>
